import React, { useEffect, useState, useContext, createContext } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import ButtonBurger from '../ButtonBurger';
import ButtonSecondary from '../ButtonSecondary';
import { Dialog, DialogDismiss, DialogHeading, useDialogState } from 'ariakit/dialog';
import { Button } from 'ariakit/button';

import { GradientContext, ContactContext, ZoomLevelContext } from '../../containers/BasePage';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import styles from './NavigationMobile.module.scss';
import { trackMenuInteraction, trackMenuContact } from '../../utils/dataLayer';

const NavContext = createContext();

const NavHeader = ({ isOpen, onClick, force }) => {
    const [hasAnimated, setHasAnimated] = useState(false);
    return (
        <>
            <Link href="/" legacyBehavior={true}>
                <a className={styles['LogoLink']}>
                    <Image
                        width={151}
                        height={36}
                        src="/img/logo.svg"
                        alt="Stockholm - capital of Scandinavia logotype, click to go to Start"
                        className={styles['Logo']}
                    />
                </a>
            </Link>
            <button
                onClick={() => {
                    onClick();
                    if (!isOpen) {
                        setTimeout(() => {
                            setHasAnimated(true);
                        }, 450);
                    } else {
                        setHasAnimated(false);
                    }
                }}
                type="button"
                aria-label={`${isOpen ? 'Close' : 'Open'} menu`}
                className={styles['Burger']}>
                <ButtonBurger isOpen={isOpen} forceOpen={force || (hasAnimated && isOpen)} />
            </button>
        </>
    );
};

const navigationMobileDefaultPropNavigation = [];

const NavigationMobile = ({ navigation = navigationMobileDefaultPropNavigation, insights = {} }) => {
    const dialog = useDialogState({ animated: true });
    const [isOpen, setIsOpen] = useState(null);
    const [megaMenuOpen, setMegaMenuOpen] = useState(null);
    const gradient = useContext(GradientContext);
    const nav = useContext(NavContext);
    const browserZoomPercentage = useContext(ZoomLevelContext);

    const rootClasses = classNames(styles['Root'], 'grid', {
        [styles['Open']]: isOpen,
    });

    const dialogClasses = classNames(styles['Dialog'], {
        [styles['IsZoomed']]: browserZoomPercentage > 100,
    });

    const navClasses = classNames(styles['NavigationMobile'], {
        [styles['Open']]: megaMenuOpen,
    });

    return (
        <header className={rootClasses}>
            <nav className={navClasses} aria-label="Meny">
                <NavHeader
                    isOpen={isOpen}
                    onClick={() => {
                        setIsOpen((pv) => !pv);
                        if (isOpen) {
                            dialog.hide();
                        } else {
                            dialog.show();
                        }
                    }}
                />
                <Dialog state={dialog} className={dialogClasses}>
                    <div className={classNames(styles['TopBlob'], styles[gradient])} />
                    <div className={`${styles['DialogContainer']} grid`}>
                        <div className={styles['LinkContainer']}>
                            {navigation.map(({ title, href, children }) => {
                                if (children.length === 0) {
                                    return (
                                        <a
                                            key={href}
                                            href={href}
                                            onClick={() => {
                                                trackMenuInteraction(1, title);
                                            }}
                                            className={styles['NavigationMobileLink']}>
                                            {title}
                                        </a>
                                    );
                                } else {
                                    return (
                                        <MegaMenu
                                            browserZoomPercentage={browserZoomPercentage}
                                            key={href}
                                            href={href}
                                            title={title}
                                            navChildren={children}
                                            insights={insights}
                                        />
                                    );
                                }
                            })}
                        </div>
                    </div>
                    <Contact />
                </Dialog>
            </nav>
        </header>
    );
};

NavigationMobile.propTypes = {
    navigation: PropTypes.array.isRequired,
};

const MegaMenu = ({ title, href, browserZoomPercentage, navChildren = [], insights }) => {
    const gradient = useContext(GradientContext);
    const [isOpen, setIsOpen] = useState(false);

    const isInsights = href === insights.url;

    const imageStyle = {
        objectFit: 'cover',
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        opacity: 0.8,
    };

    return (
        <div className={styles['MegaMenuContainer']}>
            <Button className={styles['MegaMenuButton']} onClick={() => setIsOpen(true)}>
                {title}
                <Image
                    width={8}
                    height={14}
                    src="/img/icon-chevron-right-white.svg"
                    alt=""
                    className={styles['Arrow']}
                />
            </Button>
            <div className={classNames(styles['MegaMenu'], 'grid', { [styles['IsOpen']]: isOpen })}>
                <div className={classNames(styles['TopBlob'], styles[gradient])} />
                {isOpen && (
                    <>
                        <div className={`${styles['Container']}`}>
                            <div className={`${styles['DialogContainer']}`}>
                                <Button
                                    className={styles['MegaMenuMenuButton']}
                                    onClick={() => {
                                        setIsOpen(false);
                                    }}>
                                    <Image
                                        width={16}
                                        height={10}
                                        src="/img/icon-chevron-right-white.svg"
                                        alt=""
                                        className={styles['ArrowReverse']}
                                    />
                                    Menu
                                </Button>
                                <a
                                    href={href}
                                    onClick={() => {
                                        trackMenuInteraction(1, title);
                                    }}
                                    className={styles['MegaMenuButton']}>
                                    {title}
                                </a>

                                {isInsights && (
                                    <div className={styles['ImageInsightsWrapper'] + ' black-glass'}>
                                        <Image
                                            className={styles['ImageInsights']}
                                            // src={'/img/insights-fallback-bg.webp'}
                                            src={'/img/insightsmegamenu.webp'}
                                            alt=""
                                            fill
                                            style={imageStyle}
                                            loading="lazy"
                                        />
                                        <div className={styles['ImageContentInsights']}>
                                            <h2 className={styles['ImageTitleInsights']}>
                                                {insights.title}

                                                <Image
                                                    width={20}
                                                    height={20}
                                                    src={`/img/icon-arrow-right-white.svg`}
                                                    alt=""
                                                    aria-hidden={true}
                                                    className={styles['Icon']}
                                                />
                                            </h2>
                                            <p className={styles['ImageTextInsights']}>{insights.text}</p>
                                            <Link className={styles['ImageLink']} href={insights.url}>
                                                <span className="sr-only">{insights.buttonText}</span>
                                            </Link>
                                            {/* <ButtonSecondary
                        modifiers={['InlineFlex', 'IconHoverDown']}
                        icon="icon-arrow-right-white"
                        href={insights.url}>
                        {insights.buttonText || 'Read more'}
                      </ButtonSecondary> */}
                                        </div>
                                    </div>
                                )}

                                {navChildren.map((child) => (
                                    <a
                                        key={child.href + 'mmitem'}
                                        href={child.href}
                                        onClick={() => {
                                            trackMenuInteraction(2, child.title);
                                        }}
                                        className={styles['MegaMenuLink']}>
                                        {child.title}
                                    </a>
                                ))}
                            </div>
                            {browserZoomPercentage === 100 && <Contact />}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const Contact = () => {
    const contact = useContext(ContactContext);
    const gradient = useContext(GradientContext);
    return (
        <div className={classNames(styles['MegaMenuContact'], styles[gradient])}>
            <div className={styles['ContactWrap']}>
                <div className={styles['ContactTitle']}>Contact</div>
                <div className={styles['ContactText']}>We are here to help you on your Stockholm business journey.</div>
            </div>
            {contact?.url && (
                <ButtonSecondary
                    borderColor="white"
                    asLink
                    onClick={() => {
                        trackMenuContact();
                    }}
                    href={contact?.url}
                    modifiers={['Circle']}>
                    <Image
                        width={26}
                        height={20}
                        src="/img/icon-arrow-right-white.svg"
                        alt=""
                        className={styles['Contact']}
                    />
                </ButtonSecondary>
            )}
        </div>
    );
};

export default NavigationMobile;
